import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { useCacheFilters } from 'hooks/useCacheFilters'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { camposConsultaBA, isConsultaBA } from './consultaBA'
import { formFilter } from './form'
import { FilterContracts } from './interfaces'
import { schema } from './schema'
import './style.css'
import { FormFilterElements } from './types'

const initialState: FilterContracts = {
    baseConsulta: 'DETRAN',
    ufContrato: '',
    cnpjAgenteFinanceiro: '',
    chassi: '',
    numeroGravame: '',
    numeroRestricao: '',
    numeroContrato: '',
    numeroAditivo: '',
    numeroDuda: '',
    numeroIdRegistro: '',
    dataEnvioInicial: '',
    dataEnvioFinal: '',
    tipoPesquisa: '',
    placa: '',
    cpfCnpjDevedor: '',
    tipoRestricao: ''
}

interface FilterContractProps {
    onSearch: (value: FilterContracts) => void
    isLoading: boolean
    isBackoffice: boolean
}

export function FilterContract({
    onSearch,
    isLoading,
    isBackoffice
}: FilterContractProps) {
    const {
        register,
        formState: { errors },
        watch,
        handleSubmit,
        setValue,
        reset
    } = useForm<FilterContracts>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: initialState
    })

    const { resetCacheFilters, getCacheFilters, hasCacheFilters } =
        useCacheFilters()
    const OPEN_TAB = [0]
    const CLOSE_TAB: number[] = []

    const baseConsulta = watch('baseConsulta')
    const ufContrato = watch('ufContrato')
    const consultaBA = isConsultaBA(baseConsulta, ufContrato)
    const formData = useMemo(
        () =>
            consultaBA
                ? formFilter.filter(({ input, select }) =>
                      camposConsultaBA.has(input?.id || select?.id || '')
                  )
                : formFilter,
        [consultaBA]
    )

    useEffect(() => {
        if (baseConsulta === 'SNG') {
            setValue('ufContrato', 'ES')
        }
    }, [baseConsulta, setValue])

    const defaultIndex = hasCacheFilters ? OPEN_TAB : CLOSE_TAB

    const setCacheFilters = () => {
        const cacheFilters = getCacheFilters()

        if (!cacheFilters) return

        cacheFilters?.filter &&
            Object.entries(cacheFilters.filter).forEach(([key, value]) => {
                setValue(
                    key as keyof FilterContracts,
                    value as FilterContracts[keyof FilterContracts]
                )
            })
    }

    const handleClear = () => {
        resetCacheFilters()
        reset({ ...initialState }, { keepDirtyValues: false })
    }

    const onSubmit = (value: FilterContracts) => {
        resetCacheFilters()

        onSearch({
            ...value,
            baseConsulta: value.baseConsulta || 'DETRAN',
            ufContrato: value.ufContrato?.trim().toUpperCase(),
            cnpjAgenteFinanceiro: value.cnpjAgenteFinanceiro?.replace(
                /\D/g,
                ''
            ),
            cpfCnpjDevedor: value.cpfCnpjDevedor?.replace(/\D/g, ''),
            chassi: value.chassi?.trim(),
            numeroContrato: value.numeroContrato?.trim(),
            numeroAditivo: value.numeroAditivo?.trim()
        })
    }

    useEffect(() => {
        setCacheFilters()
    }, [])

    return (
        <Accordion
            allowMultiple
            backgroundColor={'white'}
            marginBottom={4}
            defaultIndex={defaultIndex}
        >
            <AccordionItem>
                <h2>
                    <AccordionButton
                        backgroundColor={'white'}
                        className="white-hover"
                    >
                        <Box
                            as="h2"
                            flex="1"
                            textAlign="left"
                            fontSize={20}
                            fontWeight={700}
                        >
                            Filtrar contrato(s):
                        </Box>
                        <AccordionIcon />
                    </AccordionButton>
                </h2>
                <AccordionPanel pb={4} backgroundColor={'white'}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box backgroundColor={'white'}>
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                {formData.map(({ layout, input, select }) => (
                                    <Layout
                                        {...layout}
                                        key={input?.id || select?.id}
                                    >
                                        {select ? (
                                            <SelectBuild
                                                {...select}
                                                isDisable={
                                                    select.id ===
                                                        'ufContrato' &&
                                                    baseConsulta === 'SNG'
                                                        ? true
                                                        : select.isDisable
                                                }
                                                err={
                                                    errors[
                                                        select.id as FormFilterElements
                                                    ]
                                                }
                                                {...register(
                                                    select.id as FormFilterElements
                                                )}
                                            />
                                        ) : (
                                            input && (
                                                <InputBuild
                                                    {...input}
                                                    required={
                                                        consultaBA
                                                            ? input.id ===
                                                              'chassi'
                                                            : baseConsulta ===
                                                                  'SNG' &&
                                                              [
                                                                  'chassi',
                                                                  'cnpjAgenteFinanceiro',
                                                                  'numeroContrato'
                                                              ].includes(
                                                                  input.id
                                                              )
                                                            ? true
                                                            : input.id ===
                                                              'dataEnvioInicial'
                                                            ? ufContrato ===
                                                              'MT'
                                                            : input.required
                                                    }
                                                    err={
                                                        errors[
                                                            input.id as FormFilterElements
                                                        ]
                                                    }
                                                    {...register(
                                                        input.id as FormFilterElements
                                                    )}
                                                    value={watch(
                                                        input?.id as FormFilterElements
                                                    )}
                                                />
                                            )
                                        )}
                                    </Layout>
                                ))}
                            </Layout>
                            <Box
                                paddingY={8}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={6}
                            >
                                <Button
                                    colorScheme="primary"
                                    variant="outline"
                                    onClick={handleClear}
                                >
                                    Limpar Campos
                                </Button>
                                <Button
                                    colorScheme="primary"
                                    type="submit"
                                    isLoading={isLoading}
                                    isDisabled={isBackoffice}
                                >
                                    Filtrar
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    )
}
